import React, { useContext } from 'react';
import { styled } from '@mui/material';

import { NoTemplates } from 'components/organisms/TaskTemplates';
import { PageContext } from 'pages/TaskManager/PageProvider';
import {
    Button,
    Checkbox,
    Dropdown,
    Icon,
    IconButton,
    Stack,
    ScrollableTable,
    ScrollableTableHead,
    ScrollableTableBody,
    ScrollableTableRow,
    ScrollableTableCell,
    SplitButton,
    TextInput,
} from 'shell/components';
import { getDueDate } from 'utils/helper';

const Desktop = ({
    filterState,
    numFilters,
    setFilterState,
    taskTemplates,
    checked,
    onChangeCheck,
    onChangeCheckAll,
    getAssocMilestone,
    getTemplateStatusWithIcon,
    getCompletion,
    onClickTemplate,
    onClickCreate,
    onClickPublishMulti,
    onClickDeleteMulti,
    duplicateTemplate,
    deleteTemplateWithConfirmation,
}) => {
    const { openMilestone } = useContext(PageContext);

    const handleClickSplitButton = ({ key }) => {
        if (key === 'publish') onClickPublishMulti();
        if (key === 'delete') onClickDeleteMulti();
    };

    const handleSelect = ({ key }, templateId) => {
        if (key === 'edit') onClickTemplate(templateId);
        if (key === 'duplicate') duplicateTemplate(templateId);
        if (key === 'delete') deleteTemplateWithConfirmation(templateId);
    };

    return (
        <CustomStack spacing={2}>
            <Stack direction={'row'} justifyContent={'space-between'}>
                <Stack direction={'row'} spacing={2}>
                    <Button
                        color={'neutral'}
                        startIcon={<Icon kind={'filter'} />}
                        variant={'tertiary'}
                        onClick={() => setFilterState((current) => ({ ...current, isOpen: true }))}
                    >
                        {`Filters ${numFilters ? `(${numFilters})` : ''}`}
                    </Button>
                    <CustomTextInput
                        disablePointerEventsOnIcon={!Boolean(filterState.searchInput)}
                        endIcon={
                            filterState.searchInput ? (
                                <IconButton
                                    aria-label={'clear search input'}
                                    kind={'exit'}
                                    variant={'square'}
                                    onClick={() => setFilterState((current) => ({ ...current, searchInput: '' }))}
                                    onMouseDown={(e) => e.preventDefault()}
                                />
                            ) : (
                                <Icon kind={'search'} />
                            )
                        }
                        placeholder={'Search'}
                        value={filterState.searchInput}
                        onChange={(e) => setFilterState((current) => ({ ...current, searchInput: e.target.value }))}
                    />
                </Stack>
                {checked.length ? (
                    <SplitButton
                        options={[
                            { key: 'publish', value: 'Publish' },
                            { key: 'delete', value: 'Delete' },
                        ]}
                        onClick={handleClickSplitButton}
                    />
                ) : (
                    <Button
                        data-cy={'task-template-add-task-button'}
                        startIcon={<Icon kind={'add'} />}
                        onClick={onClickCreate}
                    >
                        Create
                    </Button>
                )}
            </Stack>
            {!taskTemplates.length ? (
                <NoTemplates />
            ) : (
                <CustomScrollableTable>
                    <ScrollableTableHead>
                        <ScrollableTableRow>
                            <ScrollableTableCell isFixed>
                                <Checkbox
                                    checked={taskTemplates.every(({ id }) => checked.includes(id))}
                                    onChange={(_, checked) => onChangeCheckAll(checked)}
                                />
                            </ScrollableTableCell>
                            <ScrollableTableCell isFixed>Name</ScrollableTableCell>
                            <ScrollableTableCell>Milestone Associated</ScrollableTableCell>
                            <ScrollableTableCell>Status</ScrollableTableCell>
                            <ScrollableTableCell>Due Date</ScrollableTableCell>
                            <ScrollableTableCell>Completion</ScrollableTableCell>
                            <ScrollableTableCell isFixed></ScrollableTableCell>
                        </ScrollableTableRow>
                    </ScrollableTableHead>
                    <ScrollableTableBody>
                        {taskTemplates.map((template) => (
                            <ScrollableTableRow key={template.id}>
                                <ScrollableTableCell isFixed>
                                    <Checkbox
                                        checked={checked.includes(template.id)}
                                        onChange={() => onChangeCheck(template.id)}
                                    />
                                </ScrollableTableCell>
                                <ScrollableTableCell data-cy={template.title} isFixed>
                                    <Button
                                        sx={{ textAlign: 'left' }}
                                        color={'neutral'}
                                        variant={'borderless'}
                                        onClick={() => onClickTemplate(template.id)}
                                    >
                                        {template.title}
                                    </Button>
                                </ScrollableTableCell>
                                <ScrollableTableCell>
                                    {getAssocMilestone(template) ? (
                                        <Button
                                            sx={{ textAlign: 'left' }}
                                            variant={'borderless'}
                                            onClick={() => openMilestone(template.milestone_id)}
                                        >
                                            {getAssocMilestone(template).title}
                                        </Button>
                                    ) : (
                                        '--'
                                    )}
                                </ScrollableTableCell>
                                <ScrollableTableCell>{getTemplateStatusWithIcon(template)}</ScrollableTableCell>
                                <ScrollableTableCell>{getDueDate(template)}</ScrollableTableCell>
                                <ScrollableTableCell>{getCompletion(template)}</ScrollableTableCell>
                                <ScrollableTableCell isFixed>
                                    <Dropdown
                                        id={`task-template-${template.id}`}
                                        options={[
                                            { key: 'edit', value: 'Edit' },
                                            { key: 'duplicate', value: 'Duplicate' },
                                            { key: 'delete', value: 'Delete' },
                                        ]}
                                        rootComponent={
                                            <IconButton kind={'moreHorizontal'} size={'small'} variant={'square'} />
                                        }
                                        onSelect={(option) => handleSelect(option, template.id)}
                                    />
                                </ScrollableTableCell>
                            </ScrollableTableRow>
                        ))}
                    </ScrollableTableBody>
                </CustomScrollableTable>
            )}
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        paddingTop: theme.spacing(4),
    };
});

const CustomTextInput = styled(TextInput)(() => {
    return {
        width: '13.5rem',
    };
});

const CustomScrollableTable = styled(ScrollableTable)(({ theme }) => {
    return {
        width: '100%',
        minHeight: 'calc(100vh - 206px)',
        '& .MuiTableCell-root': {
            '&:not(:last-child)': {
                borderRight: `1px solid ${theme.palette.ensightful.neutral[300]}`,
            },
            '&:nth-child(1)': {
                width: '73px',
            },
            '&:nth-child(2)': {
                width: '40%',
            },
            '&:last-child': {
                width: '56px',
            },
        },
    };
});

export default Desktop;
